<script setup lang="ts">
import { useAdminListLinkCategories, useAdminListLinkItems } from '@swipe4work/api-orval'
import { useMenuPreferences, subcategoryKey } from '@/shared/composables/UseMenuPreferences'
import { useAuthReady } from '@/shared/composables/UseAuthReady'

/*
 * Shared menu-composition controls — bound directly to the persisted preference. Reused by both the
 * MenuSettings popover and the first-run MenuSetupDialog (custom mode).
 */
const { preferences } = useMenuPreferences()
const authReady = useAuthReady()

const { data: categoriesData } = useAdminListLinkCategories({
  query: {
    staleTime: 60 * 1000,
    enabled: authReady,
  }, 
})
const { data: itemsData } = useAdminListLinkItems(
  computed(() => ({ limit: 200 })),
  {
    query: {
      staleTime: 60 * 1000,
      enabled: authReady,
    }, 
  },
)

const categoryOptions = computed(() => categoriesData.value?.categories ?? [])
const itemOptions = computed(() => itemsData.value?.items ?? [])

// Subcategory options grouped per category, so the user can pick individual subcategories.
const subcategoryOptions = computed(() => {
  const byCategory = new Map<number, {
    label: string,
    items: {
      label: string,
      value: string
    }[]
  }>()

  for (const item of itemOptions.value) {
    for (const link of item.categories) {
      const group = byCategory.get(link.id) ?? {
        label: link.name,
        items: [],
      }
      const value = subcategoryKey(link.id, link.subcategory)

      if (!group.items.some(o => o.value === value)) {
        group.items.push({
          label: link.subcategory?.trim() || 'Overig',
          value,
        })
      }

      byCategory.set(link.id, group)
    }
  }

  return [ ...byCategory.values() ].map(group => ({
    label: group.label,
    items: group.items.sort((a, b) => a.label.localeCompare(b.label)),
  }))
})
</script>

<template>
  <div class="flex flex-col gap-5">
    <div class="flex flex-col gap-2">
      <label for="menu-categories" class="text-sm text-surface-700 font-medium">
        Categorieën
      </label>
      <MultiSelect
        v-model="preferences.categoryIds"
        input-id="menu-categories"
        :options="categoryOptions"
        option-label="name"
        option-value="id"
        placeholder="Kies categorieën"
        filter
        :max-selected-labels="3"
        size="small"
        class="w-full"
      />
      <small class="text-surface-400">
        Alle items in deze categorieën verschijnen in je menu en op het dashboard.
      </small>
    </div>

    <div class="flex flex-col gap-2">
      <label for="menu-subcategories" class="text-sm text-surface-700 font-medium">
        Specifieke subcategorieën
      </label>
      <MultiSelect
        v-model="preferences.subcategoryKeys"
        input-id="menu-subcategories"
        :options="subcategoryOptions"
        option-label="label"
        option-value="value"
        option-group-label="label"
        option-group-children="items"
        placeholder="Kies subcategorieën"
        filter
        :max-selected-labels="3"
        size="small"
        class="w-full"
      />
      <small class="text-surface-400">
        Toon alleen bepaalde subcategorieën in plaats van een hele categorie.
      </small>
    </div>

    <div class="flex flex-col gap-2">
      <label for="menu-items" class="text-sm text-surface-700 font-medium">
        Losse links
      </label>
      <MultiSelect
        v-model="preferences.linkItemIds"
        input-id="menu-items"
        :options="itemOptions"
        option-label="name"
        option-value="id"
        placeholder="Kies losse links"
        filter
        :max-selected-labels="3"
        size="small"
        class="w-full"
      />
      <small class="text-surface-400">
        Voeg losse items toe bovenop je categorie-selectie.
      </small>
    </div>

    <div class="flex flex-col gap-2">
      <label for="menu-hidden" class="text-sm text-surface-700 font-medium">
        Verborgen links
      </label>
      <MultiSelect
        v-model="preferences.hiddenLinkItemIds"
        input-id="menu-hidden"
        :options="itemOptions"
        option-label="name"
        option-value="id"
        placeholder="Verberg links"
        filter
        :max-selected-labels="3"
        size="small"
        class="w-full"
      />
      <small class="text-surface-400">
        Verborgen links verdwijnen uit je menu en van het dashboard.
      </small>
    </div>
  </div>
</template>
